import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Bank, SealCheck, ClipboardText, CurrencyInr, Phone, Plus, Trash, UserCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { STAGE_LABELS, STAGE_COLORS, STAGE_ORDER, JOB_STATUS_LABELS, JOB_STATUS_COLORS, EMPANELMENT_STATUS, BANK_CATEGORIES, inr } from "@/lib/constants";
import { SectionTitle, EmptyState, Stat } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DatePicker } from "@/components/DatePicker";

export default function BranchProfile() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [contact, setContact] = useState({ name: "", designation: "", phone: "", email: "" });

  const load = () => api.get(`/branches/${id}/profile`).then((r) => { setData(r.data); setForm(r.data.branch); }).catch(() => setData(false));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const save = async () => {
    try {
      await api.put(`/branches/${id}`, { ...form, potential_value: parseFloat(form.potential_value) || 0 });
      toast.success("Branch updated");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const addContact = async () => {
    if (!contact.name) { toast.error("Enter a contact name"); return; }
    const contacts = [...(form.contacts || []), contact];
    setForm({ ...form, contacts });
    try {
      await api.put(`/branches/${id}`, { ...form, contacts, potential_value: parseFloat(form.potential_value) || 0 });
      setContact({ name: "", designation: "", phone: "", email: "" });
      toast.success("Contact added");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const removeContact = async (idx) => {
    const contacts = (form.contacts || []).filter((_, i) => i !== idx);
    try { await api.put(`/branches/${id}`, { ...form, contacts, potential_value: parseFloat(form.potential_value) || 0 }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (data === false) return <EmptyState icon={Bank}>This branch could not be found. <Link to="/app/branches" className="text-accent hover:underline">Back to all branches</Link></EmptyState>;
  if (!data || !form) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading branch profile…</div>;

  const b = data.branch;

  return (
    <div className="space-y-6" data-testid="branch-profile-page">
      <Link to="/app/branches" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-accent transition-colors duration-150" data-testid="back-to-branches">
        <ArrowLeft size={15} /> All Branches
      </Link>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-accent">Branch Profile</div>
          <h1 className="font-display font-black text-3xl md:text-4xl text-primary mt-1">{b.bank_name}</h1>
          <p className="text-sm text-muted-foreground mt-1">{b.branch_name}{b.region ? ` · ${b.region}` : ""}{b.ifsc ? ` · ${b.ifsc}` : ""}</p>
        </div>
        <span className={`text-xs px-3 py-1 rounded-full border ${STAGE_COLORS[b.stage]}`} data-testid="profile-stage">{STAGE_LABELS[b.stage]}</span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Jobs from branch" value={data.jobs.length} testid="stat-branch-jobs" />
        <Stat label="Revenue lifetime" value={inr(data.revenue_lifetime)} tone="text-accent" testid="stat-branch-lifetime" />
        <Stat label="Revenue YTD" value={inr(data.revenue_ytd)} tone="text-emerald-600" testid="stat-branch-ytd" />
        <Stat label="Open follow-ups" value={data.followups.filter((f) => f.status === "pending").length} tone="text-amber-600" testid="stat-branch-followups" />
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap h-auto" data-testid="profile-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="empanelment" data-testid="tab-empanelment">Empanelment</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts">Contact Persons</TabsTrigger>
          <TabsTrigger value="jobs" data-testid="tab-jobs">Jobs & Revenue</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <Card className="p-6 border-border space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Bank Name</Label><Input value={form.bank_name || ""} onChange={set("bank_name")} data-testid="p-bank-name" /></div>
              <div className="space-y-1.5"><Label>Branch Name</Label><Input value={form.branch_name || ""} onChange={set("branch_name")} data-testid="p-branch-name" /></div>
              <div className="space-y-1.5"><Label>IFSC</Label><Input value={form.ifsc || ""} onChange={set("ifsc")} data-testid="p-ifsc" /></div>
              <div className="space-y-1.5"><Label>Branch Code</Label><Input value={form.branch_code || ""} onChange={set("branch_code")} data-testid="p-branch-code" /></div>
              <div className="space-y-1.5"><Label>Region / Zone</Label><Input value={form.region || ""} onChange={set("region")} placeholder="e.g. Raipur City" data-testid="p-region" /></div>
              <div className="space-y-1.5"><Label>Bank Category</Label>
                <Select value={form.bank_category || "Unclassified"} onValueChange={set("bank_category")}>
                  <SelectTrigger data-testid="p-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{BANK_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Relationship Manager (ours)</Label><Input value={form.relationship_manager || ""} onChange={set("relationship_manager")} data-testid="p-rm" /></div>
              <div className="space-y-1.5"><Label>Stage</Label>
                <Select value={form.stage} onValueChange={set("stage")}>
                  <SelectTrigger data-testid="p-stage"><SelectValue /></SelectTrigger>
                  <SelectContent>{STAGE_ORDER.map((s) => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Address</Label><Textarea rows={2} value={form.address || ""} onChange={set("address")} data-testid="p-address" /></div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>Primary Contact</Label><Input value={form.contact_person || ""} onChange={set("contact_person")} /></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone || ""} onChange={set("phone")} /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input value={form.email || ""} onChange={set("email")} /></div>
            </div>
            <Button onClick={save} className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="save-profile-btn">Save Changes</Button>
          </Card>
        </TabsContent>

        <TabsContent value="empanelment">
          <Card className="p-6 border-border space-y-3">
            <SectionTitle icon={SealCheck}>Empanelment Details</SectionTitle>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Status</Label>
                <Select value={form.empanelment_status || "not_empanelled"} onValueChange={set("empanelment_status")}>
                  <SelectTrigger data-testid="p-emp-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(EMPANELMENT_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Category</Label><Input value={form.empanelment_category || ""} onChange={set("empanelment_category")} placeholder="e.g. Category A" data-testid="p-emp-cat" /></div>
              <div className="space-y-1.5"><Label>Empanelment Date</Label><DatePicker value={(form.empanelment_date || "").slice(0, 10)} onChange={set("empanelment_date")} clearable placeholder="Select date" className="w-full" data-testid="p-emp-date" /></div>
              <div className="space-y-1.5"><Label>Renewal Due</Label><DatePicker value={(form.empanelment_renewal_due || "").slice(0, 10)} onChange={set("empanelment_renewal_due")} clearable placeholder="Select date" className="w-full" data-testid="p-emp-due" /></div>
              <div className="space-y-1.5"><Label>Potential Value (₹)</Label><Input type="number" value={form.potential_value ?? ""} onChange={set("potential_value")} /></div>
            </div>
            <Button onClick={save} className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="save-emp-btn">Save Empanelment</Button>
            <p className="text-xs text-muted-foreground">Supporting documents can be attached against the related valuation jobs; renewal reminders appear on Dashboard → Alerts.</p>
          </Card>
        </TabsContent>

        <TabsContent value="contacts">
          <Card className="p-6 border-border">
            <SectionTitle icon={UserCircle}>Contact Persons</SectionTitle>
            <div className="space-y-2">
              {(form.contacts || []).length === 0 && <div className="text-sm text-muted-foreground">No additional contacts yet. Add the Branch Manager and Credit Manager below.</div>}
              {(form.contacts || []).map((c, i) => (
                <div key={`${c.name}-${i}`} className="flex flex-wrap items-center gap-2 p-3 rounded-md bg-muted border border-border" data-testid={`contact-${i}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-primary">{c.name}<span className="text-muted-foreground font-normal">{c.designation ? ` · ${c.designation}` : ""}</span></div>
                    <div className="text-xs text-muted-foreground font-mono">{c.phone}{c.email ? ` · ${c.email}` : ""}</div>
                  </div>
                  {c.phone && <a href={`tel:${c.phone}`} className="text-accent" title="Call"><Phone size={16} /></a>}
                  <button className="text-destructive" onClick={() => removeContact(i)} data-testid={`remove-contact-${i}`}><Trash size={15} /></button>
                </div>
              ))}
            </div>
            <div className="grid sm:grid-cols-4 gap-2 mt-4">
              <Input placeholder="Name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="new-contact-name" />
              <Input placeholder="Designation" value={contact.designation} onChange={(e) => setContact({ ...contact, designation: e.target.value })} data-testid="new-contact-desig" />
              <Input placeholder="Phone" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} data-testid="new-contact-phone" />
              <Input placeholder="Email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="new-contact-email" />
            </div>
            <Button onClick={addContact} variant="outline" className="rounded-full mt-3" data-testid="add-contact-btn"><Plus size={15} className="mr-1" /> Add Contact</Button>
          </Card>
        </TabsContent>

        <TabsContent value="jobs">
          <Card className="p-6 border-border">
            <SectionTitle icon={ClipboardText} right={<span className="text-xs text-muted-foreground inline-flex items-center gap-1"><CurrencyInr size={13} /> {inr(data.revenue_lifetime)} lifetime</span>}>Jobs from this Branch</SectionTitle>
            {data.jobs.length === 0 ? (
              <EmptyState icon={ClipboardText}>No valuation jobs recorded from this branch yet.</EmptyState>
            ) : (
              <div className="space-y-2">
                {data.jobs.map((j) => (
                  <div key={j.id} className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-md bg-muted border border-border" data-testid={`bp-job-${j.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-primary truncate">{j.applicant_name}</div>
                      <div className="text-xs text-muted-foreground font-mono">{j.reference} · {j.property_type}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm tabular text-primary">{inr(j.fee)}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded border ${JOB_STATUS_COLORS[j.status]}`}>{JOB_STATUS_LABELS[j.status]}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="notes">
          <Card className="p-6 border-border space-y-3">
            <SectionTitle>Notes</SectionTitle>
            <Textarea rows={6} value={form.notes || ""} onChange={set("notes")} placeholder="Meeting notes, relationship history, fee arrangements…" data-testid="p-notes" />
            <Button onClick={save} className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="save-notes-btn">Save Notes</Button>
            <div className="pt-4 border-t border-border">
              <div className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2">Related Follow-ups</div>
              {data.followups.length === 0 ? <div className="text-sm text-muted-foreground">None linked to this branch.</div> : (
                <div className="space-y-1.5">
                  {data.followups.map((f) => (
                    <div key={f.id} className="flex items-center justify-between gap-2 text-sm p-2 rounded bg-muted">
                      <span className={f.status === "done" ? "line-through text-muted-foreground" : "text-primary"}>{f.title}</span>
                      <span className="text-xs font-mono text-muted-foreground">{(f.due_date || "").slice(0, 10)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
